<template>
    <q-card :padding="$q.screen.gt.xs" bordered flat class="border-dark">
        <q-table
            ref="table"
            :data="dataRow"
            :columns="columns"
            :filter="filter"
            row-key="holder"
            :visible-columns="visibleColumns"
            hide-bottom
        >
            <!-- START CUSTOM COLUMNS -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <q-td key="holder" :props="props">{{props.row.holder}}</q-td>
                    <q-td key="owner_operator" :props="props">
                        {{props.row.owner_operator.name || '-'}}
                    </q-td>
                    <q-td key="description">{{props.row.description}}</q-td>
                    <q-td key="currency" :props="props">
                        <div class="text-bold">{{props.row.currency.symbol}}</div>
                        <small>{{props.row.currency.name}}</small>
                    </q-td>
                    <q-td key="bank" :props="props">
                        <q-item class="no-padding">
                            <q-item-section avatar>
                                <q-avatar>
                                    <q-icon :name="type_icons[props.row.type]" color="primary" size="xs"/>
                                </q-avatar>
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>
                                    {{props.row.bank ? props.row.bank : 'N/A'}}
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-td>
                    <q-td key="real_balance" :props="props">
                        <q-btn
                            round
                            outline
                            color="primary"
                            icon="fas fa-sync"
                            size="xs"
                            v-if="props.row.is_api_enabled"
                            @click="syncBalance(props.row)"
                            :loading="syncing"
                        />&nbsp;
                        <span>
                        <currency-format
                            class="text-bold inline-block"
                            :class="{'text-orange': props.row.real_balance < props.row.ideal_balance}"
                            :value="props.row.real_balance"
                            :unit="props.row.currency.symbol"
                            :decimal-max-length="4"
                        />
                        <q-popup-edit
                            v-model="props.row.real_balance"
                            buttons
                            @save="(val, initialValue) => editInline(val, initialValue, props.row, 'real_balance')"
                            v-if="$auth.can('rebit-accounts.view_filtered_and_edit_real_balance') || $auth.can('rebit-accounts.edit')"
                        >
                            <q-input type="number" v-model="props.row.real_balance" dense autofocus color="primary"/>
                        </q-popup-edit>
                    </span>
                    </q-td>
                    <q-td key="updated_at" :props="props">
                        {{props.row.updated_at}}
                    </q-td>
                </q-tr>
            </template>
            <!-- END CUSTOM COLUMNS -->
        </q-table>
    </q-card>
</template>

<script>
import RebitAccount from '../models/RebitAccount.js'
import { mapGetters } from 'vuex'
export default {
    name: 'TransactionRebitAccount',
    props: {
        operatorId: {
            type: Number,
            required: false
        },
        dataRow: {
            type: Array,
            default: () => ([])
        }
    },
    computed: {
        ...mapGetters(['currencies']),
        columns() {
            return [
                {name: 'holder', label: this.$t('rebit-accounts.table_titles.holder'), align: 'left', field: 'holder', sortable: false},
                {name: 'description', label: this.$t('rebit-accounts.table_titles.description'), align: 'left', field: 'description', sortable: true},
                {name: 'currency', label: this.$t('rebit-accounts.table_titles.currency'), align: 'left', field: 'currency.name', sortable: false},
                {name: 'bank', label: this.$t('rebit-accounts.table_titles.bank'), align: 'left', field: 'bank', sortable: false},
                {name: 'is_active', label: this.$t('rebit-accounts.table_titles.status'), align: 'center', field: 'is_active', sortable: true},
                {name: 'is_depositable', label: this.$t('rebit-accounts.table_titles.is_depositable'), align: 'center', field: 'is_depositable', sortable: true},
                {name: 'opening_balance', label: this.$t('rebit-accounts.table_titles.opening_balance'), align: 'left', field: 'opening_balance', sortable: true},
                {name: 'real_balance', label: this.$t('rebit-accounts.table_titles.real_balance'), align: 'left', field: 'real_balance', sortable: true},
                {name: 'ideal_balance', label: this.$t('rebit-accounts.table_titles.ideal_balance'), align: 'left', field: 'ideal_balance', sortable: true},
                {name: 'updated_at', label: this.$t('rebit-accounts.table_titles.updated_at'), align: 'left', field: 'updated_at', sortable: true}
            ]
        },
    },
    data: () => ({
        separator: 'horizontal',
        filter: '',
        loading: false,
        syncing: false,
        currency: null,
        visibleColumns: ['id', 'holder', 'owner_operator', 'bank', 'currency', 'real_balance', 'description', 'updated_at'],
        serverData: [],
        selection: 'none',
        selectedRows: [],
        type_icons: {
            'Account': 'fa fa-university',
            'Wallet address': 'fab fa-btc',
            'Cash': 'fa fa-coins',
        },
        hide_zero_balances: false,
        hide_inactive_accounts: true,
    }),
    methods: {
        editInline(val, initVal, row, col) {
            console.log(row)
            row[col] = initVal
            this.loading = true
            new RebitAccount({
                id: row.id,
                owner_operator_id: row.owner_operator.id,
                label: row.label,
                type: row.type,
                holder: row.holder,
                accountable_id: row.accountable_id,
                currency_acronym: row.currency.acronym,
                network: row.network,
                is_active: row.is_active,
                is_depositable: row.is_depositable,
                opening_balance: col === 'opening_balance' ? val : row.opening_balance,
                real_balance: col === 'real_balance' ? val : row.real_balance,
                ideal_balance: col === 'ideal_balance' ? val : row.ideal_balance,
                monthly_turnover_limit: col === 'monthly_turnover_limit' ? val : row.monthly_turnover_limit
            }).update((data) => {
                row[col] = val
                this.loading = false
            }).catch(({data}) => {
                this.dialog = true
                this.errorTitle = data.error
                this.errorMessages = []
                for (let err in data.errors) {
                    this.errorMessages.push(data.errors[err][0])
                }
                row[col] = initVal
                this.loading = false
            })
        },
        syncBalance(row) {
            this.syncing = true
            new RebitAccount({
                id: row.id
            }).syncRealBalance((balance) => {
                this.syncing = false
                row['real_balance'] = balance
            }).catch(({error}) => {
                this.$q.notify({
                    message: 'An error has occurred!',
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.syncing = false
            })
        },
    },
    mounted() {},
    components: {}
}
</script>
